import orderBy from 'lodash/orderBy';

import {getAlphaLabelFromIndex} from 'common/util';
import {FlowsQueryEventEntry} from 'common/query-builder/query-entry-section/types';

import {SankeyNode} from './sankey-node';
import {SankeyAnchorType, SankeyNodeType, SankeyNodeTypePriority, SankeyStepType} from './types';
import {SerializedSankeyNode} from './serialized';

export class SankeyStep {
  public nodes: SankeyNode[];
  public step: number;
  public type: SankeyStepType;
  public anchorStepIndex: number;
  public nextAnchorStepIndex: number;
  public anchorType: SankeyAnchorType;
  public canAddForwardStep: boolean;
  public canAddReverseStep: boolean;
  public anchor: number;
  public section: number;
  public queryStep: FlowsQueryEventEntry;
  private _topNodes: SankeyNode[];

  constructor(
    nodes: SerializedSankeyNode[],
    step: number,
    canAddForwardStep: boolean,
    canAddReverseStep: boolean,
    stepType: SankeyStepType,
    anchorStepIndex: number,
    nextAnchorStepIndex: number,
    anchor: number,
    alignment?: number[],
    queryStep?: FlowsQueryEventEntry,
  ) {
    this.step = step;
    this.canAddForwardStep = canAddForwardStep;
    this.canAddReverseStep = canAddReverseStep;
    this.anchorStepIndex = anchorStepIndex;
    this.nextAnchorStepIndex = nextAnchorStepIndex;
    this.anchor = anchor;

    nodes = nodes || [];
    const isFirstNodeAnchor = nodes[0] && nodes[0].type === SankeyNodeType.Anchor;

    this.type = stepType;

    this.anchorType = isFirstNodeAnchor ? nodes[0].anchorType : SankeyAnchorType.Normal;

    this.section = this.type === SankeyStepType.Forward ? anchor + 1 : anchor;

    this.nodes = (nodes || []).map(
      (node) =>
        new SankeyNode({
          serializedSankeyNode: {
            ...node,
            step: node.step || step,
          },
          letterLabel: this.letterLabel,
          anchorStepIndex: this.anchorStepIndex,
          nextAnchorStepIndex: this.nextAnchorStepIndex,
          numberOfStepsFromAnchor: this.step - this.anchorStepIndex,
          getTopNodesCallback: this.getTopNodes,
          alignment: alignment && alignment.length > 0 ? alignment[this.section] : null,
          section: this.section,
        }),
    );

    this.sortNodes();
    this.fillNodeIndeces();
    this.initTopNodes();

    this.queryStep = queryStep;
  }

  get isAnchor(): boolean {
    return this.type === SankeyStepType.Anchor;
  }

  get isNormalAnchor(): boolean {
    return this.anchorType === SankeyAnchorType.Normal && this.isAnchor;
  }

  get isForward(): boolean {
    return this.type === SankeyStepType.Forward;
  }

  get isReverse(): boolean {
    return this.type === SankeyStepType.Reverse;
  }

  get weight(): number {
    return this.nodes.reduce((sum, node) => sum + node.totalCount, 0);
  }

  getStepLabel(): string {
    if (this.isNormalAnchor) {
      let stepLabel = this.nodes.length && this.nodes[0].eventString;
      if (this.type === SankeyStepType.Anchor && this.queryStep.event) {
        if (this.queryStep.event.renamed) {
          stepLabel = this.queryStep.event.renamed;
        } else if (this.queryStep.event.custom && this.queryStep.event.label) {
          stepLabel = this.queryStep.event.label;
        }
      }
      return stepLabel;
    }

    return ``;
  }

  get letterLabel(): string {
    const alphaLabel = getAlphaLabelFromIndex(this.anchor);
    if (this.isNormalAnchor) {
      return alphaLabel;
    }

    const numStepsFromAnchor = this.step - this.anchorStepIndex;
    const sign = numStepsFromAnchor < 0 ? `-` : `+`;
    return `${alphaLabel} ${sign} ${Math.abs(numStepsFromAnchor)}`;
  }

  private fillNodeIndeces() {
    this.nodes.forEach((node, index) => (node.index = index));
  }

  private sortNodes(): void {
    this.nodes = orderBy(
      this.nodes,
      [
        // "Dropoff" and "Other events" nodes are last
        ({type}) => SankeyNodeTypePriority[type],
        // nodes with greatest weight come first
        `totalCount`,
        // break count ties by sorting event name
        `eventString`,
      ],
      [`desc`, `desc`, `asc`],
    );
  }

  private getTopNodes = () => {
    return this._topNodes;
  };

  // Sets the Top Nodes list for this Step.
  private initTopNodes = () => {
    // The Top Nodes are all the nodes for this step that have their own row (aka are not pruned or a drop-off).
    const topNodes: SankeyNode[] = [];
    this.nodes.forEach((node) => {
      if (node.type !== SankeyNodeType.Pruned && node.type !== SankeyNodeType.Dropoff) {
        topNodes.push(node);
      }
    });

    // Set the topNodes value.
    this._topNodes = topNodes;
  };
}
